import React, { FC, useEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KButton, KSpan, KTooltip, KInput } from "kahuna-base-react-components"
import { handleAuthorizedRequest, tracks, users } from "../../../../../apis"
import { mapListToCamelCase, mapToCamelCase } from "../../../../../utility"
import { MissingTrackShareHolder, Track, TrackShareHolder, User } from "../../../../../types"
import { errorFilledToastOptions } from "../../../../../constants"
import { toast } from "react-toastify"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

type AdminMissingShareHoldersProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
  missingTrackShareHolderCount: number
  updateMissingTrackShareHolderCount: (count: number) => Promise<void>
}

const AdminMissingShareHolders: FC<AdminMissingShareHoldersProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [missingTrackShareHolder, setMissingTrackShareHolder] = useState<MissingTrackShareHolder>()
  const [isrc, setIsrc] = useState<string>("")
  const [timeoutId, setTimeoutId] = useState(null)
  const [searchUserOptions, setSearchUserOptions] = useState<KSelectOption[]>([])
  const [searchText, setSearchText] = useState<string>("")

  const fetchMissingShareHolder = async () => {
    setLoading(true)
    tracks(`/missing_share_holders/?isrc=${isrc}`, "GET").then((resp) => {
      if (resp.data.result) {
        const data: MissingTrackShareHolder = mapToCamelCase(resp.data.result)
        setMissingTrackShareHolder(data)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchMissingShareHolder()
  }, [])

  const onSelectReleaseUser = (user: User) => {
    if (!missingTrackShareHolder) return
    if (
      !window.confirm(`Are you sure you want to select ${user.firstName} ${user.lastName} as the track main owner?`)
    ) {
      return
    }
    tracks(`/save_main_share_holder/`, "POST", {
      track_id: missingTrackShareHolder.id,
      track_title: missingTrackShareHolder.title,
      user_id: user.id
    }).then((resp) => {
      if (resp.data.success) {
        toast.success("Main share holder updated successfully.")
        props.updateMissingTrackShareHolderCount(props.missingTrackShareHolderCount - 1)
        fetchMissingShareHolder()
      } else {
        toast.error(`Error updating main share holder: ${resp.data.message}`, errorFilledToastOptions)
      }
    })
  }

  const onClickSpotifyColumn = (upc: string) => {
    setLoading(true)
    users(`/get_spotify_album_link/?upc=${upc}`, "GET")
      .then((resp) => {
        setLoading(false)
        if (resp && resp?.data && resp?.data?.result) {
          const albumLink = resp.data.result
          window.open(albumLink, "_blank")
        } else {
          toast.error("Spotify link for this album could not be found.", errorFilledToastOptions)
        }
      })
      .catch(() => {
        toast.error("Error getting the Spotify link", errorFilledToastOptions)
      })
  }

  const onSearchTextChanged = async (text: string) => {
    setSearchText(text)
    if (!text) return
    setSearchUserOptions([])
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setSearchUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const renderMissingTrackShareHolders = () => {
    const item = missingTrackShareHolder

    if (!item) {
      return (
        <div>
          <KSpan text="No missing track share holders found." fontSize={16} lineHeight="24px" />
        </div>
      )
    }

    return (
      <div className="p-4 border border-gray-300 rounded-lg shadow-sm bg-white w-full" style={{ width: "100%" }}>
        {/* TOP SECTION */}
        <div className="flex gap-4">
          {/* Cover */}

          {/* Metadata */}
          <div className="flex gap-4" style={{ width: "100%" }}>
            <div className="flex flex-col gap-2" style={{ width: "70%" }}>
              <div className="flex gap-3">
                <div className="w-72 h-72">
                  <img src={item.release?.coverFileUrl} alt="Cover" className="rounded" />
                  <div className="flex ml-12 gap-10 no-parent-trigger mt-6">
                    <div style={{ display: "grid", alignItems: "center" }}>
                      <img
                        onClick={() =>
                          window.open(`https://labelcamp.idol.io/catalog/products/${item.release?.productId}`, "_blank")
                        }
                        src={"/catalog_icons/idol.png"}
                        alt={`${item.release?.id}_idol`}
                        style={{ width: 32, height: "fit-content", cursor: "pointer" }}
                      />
                    </div>
                    <div style={{ display: "grid", alignItems: "center" }}>
                      <img
                        onClick={() => {
                          if (loading || !item.release?.upc) {
                            return
                          }
                          onClickSpotifyColumn(item.release.upc)
                        }}
                        src={"/account_icons/social_media_icons/spotify.svg"}
                        alt={`${item.release?.id}_spotify_link`}
                        style={{ width: 32, cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ width: "100%" }}>
                  <div className="flex gap-3" style={{ width: "100%" }}>
                    <div className="flex flex-col gap-2" style={{ width: "100%" }}>
                      <KTitleSpan text={"Track"} fontSize={18} lineHeight="26px" />
                      <KSpan text={`Title: ${item.title}`} fontSize={14} lineHeight="20px" />
                      <KSpan text={`ISRC: ${item.isrc}`} fontSize={14} lineHeight="20px" />
                      <KSpan text={`Order: ${item.number}`} fontSize={14} lineHeight="20px" />
                    </div>
                    <div className="flex flex-col gap-2" style={{ width: "100%" }}>
                      <div>
                        <KTitleSpan text={"Release"} fontSize={18} lineHeight="26px" />
                      </div>
                      <KSpan text={`Title: ${item.release?.title}`} />
                      <KSpan text={`UPC: ${item.release?.upc}`} fontSize={14} lineHeight="20px" />
                      <KSpan text={`# of Tracks: ${item.release?.totalTrack}`} fontSize={14} lineHeight="20px" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <KTitleSpan text={"Track Share Holders"} fontSize={16} lineHeight="24px" />
                  </div>
                  {item.trackShareHolderList.length === 0 && <KSpan text="-" />}
                  {item.trackShareHolderList.map((trackShareHolder) => {
                    const color = trackShareHolder.status === "Active" ? "rgba(26, 173, 45, 1)" : "#cec51eff"
                    return (
                      <div key={trackShareHolder.id} className="flex flex-col gap-1" style={{ width: "68%" }}>
                        <div className="flex justify-between">
                          <KSpan
                            color={color}
                            text={`${trackShareHolder.user?.firstName} ${trackShareHolder.user?.lastName || ""}`}
                          />
                          <KSpan color={color} text={`(${trackShareHolder.status}) ${trackShareHolder.share}%`} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1" style={{ width: "30%" }}>
              <KTitleSpan fontSize={18} lineHeight="26px" text="Contributors" />
              {Object.entries(item.contributors || {}).map(([role, name]) => (
                <div key={`${role}_contributor`}>
                  <KSpan text={`${role}: `} fontSize={14} lineHeight="20px" />
                  <KSpan text={`${name || "-"}`} fontSize={14} lineHeight="20px" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* DIVIDER */}
        <div className="my-4 h-px w-full bg-gray-200" />

        {/* RELEASE USERS SECTION */}
        <div className="flex gap-16" style={{ width: "100%" }}>
          <div className="flex flex-col gap-2" style={{ width: "50%" }}>
            <KTitleSpan text="Possible Release Users" fontSize={16} lineHeight="24px" />

            <div className="mt-2 flex flex-col gap-2 mb-4">
              {item.releaseUserList.length === 0 && <KSpan text="-" />}
              {(item.releaseUserList || []).map((releaseUser) => (
                <div
                  key={releaseUser.userId}
                  onClick={() => onSelectReleaseUser(releaseUser.user)}
                  className="flex gap-1 cursor-pointer p-3 border border-gray-200 rounded hover:bg-gray-100 transition"
                >
                  <KSpan text={`${releaseUser.user.firstName}`} fontSize={14} lineHeight="20px" />
                  <KSpan text={`${releaseUser.user.lastName}`} fontSize={14} lineHeight="20px" />
                  <KSpan text={`(${releaseUser.user.email})`} fontSize={14} lineHeight="20px" />
                </div>
              ))}
            </div>

            <KTitleSpan text="Search User" fontSize={16} lineHeight="24px" />

            <div className="mt-2 flex flex-col gap-2">
              <KInput
                dataTestId="search-user-input"
                placeholder={"Not found? Search user by email"}
                onChange={(text) => onSearchTextChanged(text)}
                height={24}
                value={searchText}
              />
            </div>
            {searchUserOptions.map((option) => (
              <div
                key={option.value}
                onClick={() =>
                  onSelectReleaseUser({
                    id: option.value as number,
                    firstName: option.label,
                    lastName: "",
                    email: ""
                  } as User)
                }
                className="flex gap-1 cursor-pointer p-3 border border-gray-200 rounded hover:bg-gray-100 transition"
              >
                <KSpan text={option.label} fontSize={14} lineHeight="20px" />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2" style={{ width: "50%" }}>
            <KTitleSpan text="Other Tracks Share Holders in Album" fontSize={16} lineHeight="24px" />

            <div className="mt-2 flex flex-col gap-2">
              {item.releaseShareHolderList.length === 0 && <KSpan text="-" />}
              {(item.releaseShareHolderList || []).map((rsh: TrackShareHolder) => (
                <div
                  key={`${Math.random()}`}
                  onClick={() => onSelectReleaseUser(rsh.user)}
                  className="cursor-pointer p-3 border border-gray-200 rounded hover:bg-gray-100 transition flex flex-col gap-2"
                >
                  <div className="flex gap-1">
                    <KSpan color="#111" fontWeight={500} text={`User: `} />
                    <KSpan text={`${rsh.user.firstName}`} fontWeight={500} fontSize={14} lineHeight="20px" />
                    <KSpan text={`${rsh.user.lastName}`} fontSize={14} lineHeight="20px" />
                    <KSpan text={`(${rsh.user.email})`} fontSize={14} lineHeight="20px" />
                  </div>
                  <div className="flex gap-1">
                    <KSpan fontWeight={500} color="#111" text={`Track: `} />
                    <KSpan text={`${rsh.track.title}`} fontSize={14} lineHeight="20px" />
                    <KSpan text={`(${rsh.track.isrc})`} fontSize={14} lineHeight="20px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="catalog flex flex-col bg-[#fff] px-8 pt-4"
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          fetchMissingShareHolder()
        }
      }}
    >
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"Missing Track Main Share Holders"} />
          </div>
        </KTooltip>
        <div className="flex gap-4">
          <KInput placeholder="ISRC" onChange={(text) => setIsrc(text)} value={isrc} width={200} />
          <KButton
            text="Apply"
            onClick={() => fetchMissingShareHolder()}
            background="black"
            textColor="white"
            width="120px"
          />
        </div>
      </div>

      {renderMissingTrackShareHolders()}
    </div>
  )
}

export default AdminMissingShareHolders
